/**
 * MEMCORE Modul B (Labor) — „Mein Gedächtnis“ (UI Hybrid B, SKALIERUNG §7.6).
 *
 * Liste der eigenen aktiven Vermerke (PB-Lesepfad, Rule scoped auf
 * @request.auth.id). Skills manuell anlegen (Freitext ± Chip), Bearbeiten =
 * neue Version (Curator supersede), Löschen = Tombstone. Schreiben läuft
 * ausschließlich über die Curator-Schreibstraße.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  BookMarked, Loader2, Pencil, Trash2, RefreshCw, AlertTriangle, CheckCircle2, Sparkles, X, Plus,
} from 'lucide-react';
import { PORTAL_BTN } from '../../arkorbit-ui/portalTheme';
import { getPortalPocketBaseUrl, getPortalPbToken } from '../../../constants';
import {
  type MemVermerk,
  type MemcoreContext,
  type SkillChipId,
  SKILL_CHIPS,
  curatorFehlertext,
  isSkillChipVermerk,
  isSkillVermerk,
  listMeineVermerke,
  memcoreSkillSpeichern,
  memcoreSupersede,
  memcoreTombstone,
  skillChipIdFromQuelleRef,
} from '../memcore';

export interface MemoryViewProps {
  ctx: MemcoreContext;
  curatorUrl: string;
  /** Onboarding-Interview erneut starten (Erststart-Dialog im CustomerPortal). */
  onStartOnboarding: () => void;
}

function formatDatum(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('de-AT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso.slice(0, 10);
  }
}

const VERTRAUEN_BADGE: Record<string, string> = {
  belegt: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
  abgeleitet: 'border-sky-500/40 bg-sky-500/10 text-sky-300',
  vermutet: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
};

function skillBadgeLabel(v: MemVermerk): string | null {
  if (!isSkillVermerk(v)) return null;
  const chipId = skillChipIdFromQuelleRef(v.quelle_ref);
  if (chipId) {
    const chip = SKILL_CHIPS.find((c) => c.id === chipId);
    return chip ? `Skill · ${chip.label}` : 'Skill · Chip';
  }
  return 'Skill · Freitext';
}

export const MemoryView: React.FC<MemoryViewProps> = ({ ctx, curatorUrl, onStartOnboarding }) => {
  const [vermerke, setVermerke] = useState<MemVermerk[]>([]);
  const [loading, setLoading] = useState(false);
  const [fehler, setFehler] = useState('');
  const [hinweis, setHinweis] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const [skillOpen, setSkillOpen] = useState(false);
  const [skillText, setSkillText] = useState('');
  const [skillChip, setSkillChip] = useState<SkillChipId | null>(null);
  const [skillBusy, setSkillBusy] = useState(false);

  const laden = useCallback(async () => {
    setLoading(true);
    setFehler('');
    try {
      const token = getPortalPbToken();
      if (!token) {
        throw new Error('Keine aktive Portal-Sitzung — bitte neu anmelden.');
      }
      const items = await listMeineVermerke(getPortalPocketBaseUrl(), token, ctx.userId);
      setVermerke(items);
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : 'Gedächtnis konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [ctx.userId]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const startEdit = (v: MemVermerk) => {
    setEditId(v.vermerk_id);
    setEditText(v.inhalt);
    setConfirmDeleteId(null);
    setHinweis('');
  };

  const speichereEdit = async (v: MemVermerk) => {
    const neu = editText.trim();
    if (!neu || neu === v.inhalt) {
      setEditId(null);
      return;
    }
    setBusyId(v.vermerk_id);
    const r = await memcoreSupersede(curatorUrl, ctx, v.vermerk_id, neu);
    setBusyId(null);
    if (r.status === 'ok') {
      setHinweis(`Fortgeschrieben als ${r.vermerk_id ?? 'neue Version'} (alte Version bleibt im Register).`);
      setEditId(null);
      void laden();
    } else {
      setFehler(curatorFehlertext(r));
    }
  };

  const loesche = async (v: MemVermerk) => {
    setBusyId(v.vermerk_id);
    const r = await memcoreTombstone(curatorUrl, ctx, v.vermerk_id);
    setBusyId(null);
    setConfirmDeleteId(null);
    if (r.status === 'ok') {
      setHinweis(`Vermerk ${v.vermerk_id} gelöscht.`);
      setVermerke((prev) => prev.filter((x) => x.vermerk_id !== v.vermerk_id));
    } else {
      setFehler(curatorFehlertext(r));
    }
  };

  const speichereSkill = async () => {
    const text = skillText.trim();
    if (!text) return;
    setSkillBusy(true);
    setFehler('');
    const r = await memcoreSkillSpeichern(curatorUrl, ctx, text, skillChip);
    setSkillBusy(false);
    if (r.status === 'ok' || r.status === 'duplikat') {
      const chipLabel = skillChip
        ? SKILL_CHIPS.find((c) => c.id === skillChip)?.label
        : null;
      setHinweis(
        r.status === 'duplikat'
          ? 'Bereits vorhanden — kein Duplikat angelegt.'
          : chipLabel
            ? `Skill gespeichert (Chip „${chipLabel}“ → immer im ARKO-Kontext).`
            : 'Skill gespeichert (Freitext → Recall bei Bedarf).',
      );
      setSkillText('');
      setSkillChip(null);
      setSkillOpen(false);
      void laden();
    } else {
      setFehler(curatorFehlertext(r));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-base font-bold text-white">
            <BookMarked className="h-4 w-4 text-blue-400" aria-hidden />
            Mein Gedächtnis
          </h2>
          <p className="mt-1 text-xs leading-relaxed text-slate-500">
            Skills für ARKOs Verhalten — nur Ihr Sitz, keine Akten oder Mandantendaten.
            Chip-Skills gehen fest in den Kontext; Freitext ohne Chip nur bei Recall.
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setSkillOpen((o) => !o);
              setFehler('');
              setHinweis('');
            }}
            className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs ${PORTAL_BTN.secondary}`}
            title="Skill für ARKO-Verhalten hinzufügen"
          >
            <Plus className="h-3.5 w-3.5" />
            Skill hinzufügen
          </button>
          <button
            type="button"
            onClick={onStartOnboarding}
            className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs ${PORTAL_BTN.secondary}`}
            title="Onboarding-Interview (erneut) durchlaufen"
          >
            <Sparkles className="h-3.5 w-3.5" />
            Interview
          </button>
          <button
            type="button"
            onClick={() => void laden()}
            disabled={loading}
            className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs ${PORTAL_BTN.secondary}`}
            title="Neu laden"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </button>
        </div>
      </div>

      {skillOpen && (
        <div className="rounded-xl border border-blue-500/25 bg-slate-900/80 px-4 py-3 space-y-3">
          <p className="text-xs font-semibold text-slate-200">Neuer Skill</p>
          <p className="text-[11px] leading-relaxed text-slate-500">
            Freitext beschreibt, wie ARKO arbeiten soll. Optionaler Chip markiert die Kategorie
            und stellt sicher, dass der Skill bei jeder Nachricht im Kontext landet.
          </p>
          <textarea
            value={skillText}
            onChange={(e) => setSkillText(e.target.value)}
            rows={3}
            placeholder="z. B. Formuliere knapp und ohne Floskeln."
            className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm leading-relaxed text-slate-100 placeholder:text-slate-600 focus:border-blue-400 focus:outline-none"
          />
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Chip (optional)</span>
            {SKILL_CHIPS.map((chip) => {
              const selected = skillChip === chip.id;
              return (
                <button
                  key={chip.id}
                  type="button"
                  onClick={() => setSkillChip(selected ? null : chip.id)}
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                    selected
                      ? 'border-blue-400/50 bg-blue-500/15 text-blue-200'
                      : 'border-slate-700 bg-slate-900 text-slate-400 hover:border-slate-500 hover:text-slate-200'
                  }`}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setSkillOpen(false);
                setSkillText('');
                setSkillChip(null);
              }}
              disabled={skillBusy}
              className="rounded-lg px-2.5 py-1 text-xs text-slate-400 hover:text-slate-200"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={() => void speichereSkill()}
              disabled={skillBusy || !skillText.trim()}
              className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-semibold ${PORTAL_BTN.primary}`}
            >
              {skillBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
              Speichern
            </button>
          </div>
        </div>
      )}

      {fehler && (
        <div className="flex items-start justify-between gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
          <span className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            {fehler}
          </span>
          <button type="button" onClick={() => setFehler('')} aria-label="Hinweis schließen">
            <X className="h-3.5 w-3.5 text-rose-300/70 hover:text-rose-200" />
          </button>
        </div>
      )}
      {hinweis && (
        <div className="flex items-start justify-between gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200">
          <span className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            {hinweis}
          </span>
          <button type="button" onClick={() => setHinweis('')} aria-label="Hinweis schließen">
            <X className="h-3.5 w-3.5 text-emerald-300/70 hover:text-emerald-200" />
          </button>
        </div>
      )}

      {loading && vermerke.length === 0 && (
        <div className="flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-6 text-sm text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Gedächtnis wird geladen…
        </div>
      )}

      {!loading && !fehler && vermerke.length === 0 && !skillOpen && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-6 text-center">
          <p className="text-sm text-slate-300">Noch keine Vermerke.</p>
          <p className="mt-1 text-xs text-slate-500">
            Skill hinzufügen, Interview starten oder im ARKO-Chat per „Session beenden“
            bzw. Daumen hoch speichern.
          </p>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setSkillOpen(true)}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold ${PORTAL_BTN.primary}`}
            >
              <Plus className="h-3.5 w-3.5" />
              Skill hinzufügen
            </button>
            <button
              type="button"
              onClick={onStartOnboarding}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs ${PORTAL_BTN.secondary}`}
            >
              <Sparkles className="h-3.5 w-3.5" />
              Onboarding starten
            </button>
          </div>
        </div>
      )}

      <ul className="space-y-2">
        {vermerke.map((v) => {
          const busy = busyId === v.vermerk_id;
          const editing = editId === v.vermerk_id;
          const skillLabel = skillBadgeLabel(v);
          return (
            <li
              key={v.id || v.vermerk_id}
              className="rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-3"
            >
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] text-slate-500">{v.vermerk_id}</span>
                {skillLabel && (
                  <span
                    className={`rounded-full border px-1.5 py-0 text-[9px] font-semibold tracking-wide ${
                      isSkillChipVermerk(v)
                        ? 'border-violet-500/40 bg-violet-500/10 text-violet-200'
                        : 'border-slate-600 bg-slate-800 text-slate-300'
                    }`}
                    title={
                      isSkillChipVermerk(v)
                        ? 'Chip-Skill: landet bei jedem ARKO-Send im Kontext'
                        : 'Freitext-Skill: nur bei Recall'
                    }
                  >
                    {skillLabel}
                  </span>
                )}
                {v.vertrauen && (
                  <span
                    className={`rounded-full border px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wider ${
                      VERTRAUEN_BADGE[v.vertrauen] ?? 'border-slate-600 bg-slate-800 text-slate-400'
                    }`}
                  >
                    {v.vertrauen}
                  </span>
                )}
                {v.ersetzt && (
                  <span className="text-[9px] text-slate-600" title={`Fortschreibung von ${v.ersetzt}`}>
                    ersetzt {v.ersetzt}
                  </span>
                )}
                <span className="ml-auto text-[10px] text-slate-600">
                  seit {formatDatum(v.gueltig_von)}
                </span>
                <button
                  type="button"
                  onClick={() => (editing ? setEditId(null) : startEdit(v))}
                  disabled={busy}
                  className="rounded p-1 text-slate-500 transition-colors hover:bg-white/[0.06] hover:text-blue-300"
                  title="Bearbeiten (neue Version)"
                  aria-label={`Vermerk ${v.vermerk_id} bearbeiten`}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDeleteId(confirmDeleteId === v.vermerk_id ? null : v.vermerk_id)}
                  disabled={busy}
                  className="rounded p-1 text-slate-500 transition-colors hover:bg-white/[0.06] hover:text-rose-300"
                  title="Löschen"
                  aria-label={`Vermerk ${v.vermerk_id} löschen`}
                >
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                </button>
              </div>

              {!editing && (
                <p className="mt-2 text-sm leading-relaxed text-slate-200 whitespace-pre-wrap">{v.inhalt}</p>
              )}

              {editing && (
                <div className="mt-2 space-y-2">
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    rows={3}
                    className="w-full rounded-lg border border-blue-500/40 bg-slate-950 px-3 py-2 text-sm leading-relaxed text-slate-100 focus:border-blue-400 focus:outline-none"
                  />
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setEditId(null)}
                      disabled={busy}
                      className="rounded-lg px-2.5 py-1 text-xs text-slate-400 hover:text-slate-200"
                    >
                      Abbrechen
                    </button>
                    <button
                      type="button"
                      onClick={() => void speichereEdit(v)}
                      disabled={busy || !editText.trim()}
                      className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-semibold ${PORTAL_BTN.primary}`}
                    >
                      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                      Als neue Version speichern
                    </button>
                  </div>
                </div>
              )}

              {confirmDeleteId === v.vermerk_id && !editing && (
                <div className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2">
                  <span className="text-xs text-rose-200">
                    Wirklich löschen? ARKO nutzt diesen Vermerk danach nicht mehr.
                  </span>
                  <div className="flex flex-shrink-0 items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(null)}
                      className="rounded px-2 py-1 text-xs text-slate-400 hover:text-slate-200"
                    >
                      Abbrechen
                    </button>
                    <button
                      type="button"
                      onClick={() => void loesche(v)}
                      disabled={busy}
                      className="rounded bg-rose-600 px-2 py-1 text-xs font-semibold text-white hover:bg-rose-500"
                    >
                      Löschen
                    </button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default MemoryView;
